import InfoIcon from '@mui/icons-material/Info';
import { TreeItem } from '@mui/lab';
import {
  Box,
  Checkbox,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { every, isArray, isEmpty, map, some, toString } from 'lodash';
import React, { useState } from 'react';

import CopyableText from 'components/atoms/CopyableText';
import { JobDetails } from 'components/Pages/Jobs/JobDetails';
import { OrchestrationInference } from 'interfaces/calculateFeatures';
import { InferenceJob, Job, JobType } from 'interfaces/job';
import { Slide } from 'interfaces/slide';
import { formatDateTimeForUser } from 'utils/dateTime';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { useJobsByOrchestrationIdContext } from '../../../JobsByOrchestrationIdContext';

export interface OrchestrationRowProps {
  orchestration: OrchestrationInference;
  modelUrl?: string;
  modelType?: string;
  resultName?: string;
  selectedOrchestrations: OrchestrationBySlideByType | OrchestrationBySlideByResultName;
  onCheck: (orchestration: OrchestrationInference, slideIds: string[], checked: boolean) => void;
  slides: Record<string, Pick<Slide, 'stainingType' | 'originalFileName'>>;
  studyId: string;
  disableIfNoJob?: boolean;
}

const NO_SLIDE_IDS: string[] = [];

const OrchestrationRow: React.FC<React.PropsWithChildren<OrchestrationRowProps>> = ({
  orchestration,
  modelUrl,
  modelType,
  resultName,
  selectedOrchestrations,
  onCheck,
  slides,
  disableIfNoJob = false,
}) => {
  // if the orchestration is an inference model, we use the model type, otherwise we use the result name
  const groupedByName = modelType ?? resultName;
  if (!groupedByName) {
    console.error('Inference model type or result name is required');
    return null;
  }

  const placeholderDataJob = {
    orchestrationId: orchestration.orchestrationId,
    params: {
      modelPaths: modelUrl ? [modelUrl] : [],
      tissueSegmentationModelOverride: orchestration.params.deps?.modelTsm,
      useDynamicCellDetection: orchestration.params.deps?.useDynamicCellDetection,
      dynamicCellDetectionConfig: orchestration.params.deps?.dynamicCellDetectionConfig,
      dedupValue: toString(orchestration.params.deps?.dedupValue),
    },
    type: JobType.Inference,
  } as InferenceJob;

  // Jobs for every orchestration in this study are fetched once, in a small number of batched
  // requests, by JobsByOrchestrationIdProvider - avoids issuing one /jobs request per row.
  const {
    jobsByOrchestrationId,
    isLoadingJobs: isLoading,
    isFetchingJobs: isFetching,
  } = useJobsByOrchestrationIdContext();

  const jobDataFromDB = jobsByOrchestrationId[orchestration.orchestrationId];
  const jobData: Job = jobDataFromDB || placeholderDataJob;
  const jobTitle = isLoading || isFetching ? 'Loading job details...' : jobDataFromDB?.name || 'Untitled job';
  const jobStartTime = formatDateTimeForUser(jobDataFromDB?.startedAt);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const orchestrationSlideIds = isArray(orchestration?.slideIds) ? orchestration.slideIds : NO_SLIDE_IDS;
  const numOrchestrationSlideIds = orchestrationSlideIds.length;
  const orchestrationId = orchestration?.orchestrationId;
  const isOrchestrationChecked = every(
    orchestrationSlideIds,
    (slideId) => selectedOrchestrations[slideId]?.[groupedByName]?.orchestration?.orchestrationId === orchestrationId
  );

  return (
    <>
      <TreeItem
        nodeId={orchestrationId}
        label={
          <Tooltip
            title={
              disableIfNoJob &&
              (isLoading || isFetching
                ? 'Loading job information...'
                : isEmpty(jobDataFromDB)
                ? 'No job found for this orchestration'
                : '')
            }
          >
            <ListItem
              component="div"
              disableGutters
              dense
              secondaryAction={
                <Tooltip title="View job parameters">
                  <IconButton aria-label="View job parameters" size="small" onClick={handleClick}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              sx={{ alignItems: 'flex-start', maxWidth: '100%', minWidth: 0, pr: 4, py: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                <Checkbox
                  indeterminate={
                    !isOrchestrationChecked &&
                    some(
                      orchestrationSlideIds,
                      (slideId) =>
                        selectedOrchestrations[slideId]?.[groupedByName]?.orchestration?.orchestrationId ===
                        orchestrationId
                    )
                  }
                  checked={isOrchestrationChecked}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onCheck(orchestration, orchestrationSlideIds, event.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  disabled={disableIfNoJob && isEmpty(jobDataFromDB)}
                  size="small"
                  sx={{ p: 0 }}
                />
              </ListItemIcon>
              <ListItemText
                sx={{ my: 0, minWidth: 0 }}
                primary={jobTitle}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 700, noWrap: true, title: jobTitle }}
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <Box component="span" display="block" maxWidth="100%" minWidth={0} sx={{ overflowWrap: 'anywhere' }}>
                    <Box display="flex" flexWrap="wrap" alignItems="center" columnGap={1}>
                      {jobDataFromDB?.id && (
                        <CopyableText
                          variant="caption"
                          color="text.secondary"
                          textForCopy={jobDataFromDB.id}
                          tooltipText="Copy job ID"
                          copyWithButton="right"
                          copyButtonProps={{ 'aria-label': 'Copy job ID', size: 'small', sx: { ml: 0.25, p: 0 } }}
                          copyIconProps={{ fontSize: 'inherit' }}
                          parentGridProps={{ width: 'fit-content', justifyContent: 'flex-start' }}
                        >
                          Job ID: {jobDataFromDB.id}
                        </CopyableText>
                      )}
                      {jobStartTime && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          Started {jobStartTime}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" flexWrap="wrap" alignItems="center" columnGap={1}>
                      <CopyableText
                        variant="caption"
                        color="text.secondary"
                        textForCopy={orchestrationId}
                        tooltipText="Copy orchestration ID"
                        copyWithButton="right"
                        copyButtonProps={{
                          'aria-label': 'Copy orchestration ID',
                          size: 'small',
                          sx: { ml: 0.25, p: 0 },
                        }}
                        copyIconProps={{ fontSize: 'inherit' }}
                        parentGridProps={{
                          width: 'fit-content',
                          justifyContent: 'flex-start',
                          sx: { maxWidth: '100%' },
                        }}
                        sx={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                      >
                        Orchestration ID: {orchestrationId}
                      </CopyableText>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {numOrchestrationSlideIds} slide{numOrchestrationSlideIds !== 1 && 's'}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          </Tooltip>
        }
      >
        {map(orchestrationSlideIds, (slideId) => {
          return (
            <TreeItem
              key={`${orchestrationId}-${slideId}`}
              nodeId={`${orchestrationId}-${slideId}`}
              label={
                <ListItem component="div" disableGutters dense sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      checked={
                        selectedOrchestrations[slideId]?.[groupedByName]?.orchestration?.orchestrationId ===
                        orchestrationId
                      }
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        onCheck(orchestration, [slideId], event.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      disabled={disableIfNoJob && isEmpty(jobDataFromDB)}
                      size="small"
                      sx={{ p: 0 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{ my: 0, minWidth: 0 }}
                    primary={
                      <Box display="flex" flexWrap="wrap" alignItems="center" columnGap={1}>
                        <CopyableText
                          variant="body2"
                          textForCopy={slideId}
                          tooltipText="Copy slide ID"
                          copyWithButton="right"
                          copyButtonProps={{ 'aria-label': 'Copy slide ID', size: 'small', sx: { ml: 0.25, p: 0 } }}
                          copyIconProps={{ fontSize: 'inherit' }}
                          parentGridProps={{
                            width: 'fit-content',
                            justifyContent: 'flex-start',
                            sx: { maxWidth: '100%' },
                          }}
                          sx={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                        >
                          Slide ID: {slideId}
                        </CopyableText>
                        <Typography component="span" variant="body2">
                          | {slides?.[slideId]?.originalFileName || 'Unknown file'}
                        </Typography>
                      </Box>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              }
            />
          );
        })}
      </TreeItem>
      <Popover
        onClick={(e) => e.stopPropagation()}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: { width: '500px' },
        }}
      >
        <JobDetails job={jobData} onClosed={handleClose} />
      </Popover>
    </>
  );
};

export default OrchestrationRow;
